/** Colores de evento de Google Calendar (colorId 1–11). Hex aproximados de la UI web. */
export const GOOGLE_EVENT_COLORS = [
  { id: "1", label: "Lavanda", hex: "#a4bdfc" },
  { id: "2", label: "Salvia", hex: "#7ae7bf" },
  { id: "3", label: "Uva", hex: "#dbadff" },
  { id: "4", label: "Flamenco", hex: "#ff887c" },
  { id: "5", label: "Plátano", hex: "#fbd75b" },
  { id: "6", label: "Mandarina", hex: "#ffb878" },
  { id: "7", label: "Pavo real", hex: "#46d6db" },
  { id: "8", label: "Grafito", hex: "#e1e1e1" },
  { id: "9", label: "Arándano", hex: "#5484ed" },
  { id: "10", label: "Albahaca", hex: "#51b749" },
  { id: "11", label: "Tomate", hex: "#dc2127" },
] as const;

export type GoogleEventColorId = (typeof GOOGLE_EVENT_COLORS)[number]["id"];

const VALID_IDS = new Set(GOOGLE_EVENT_COLORS.map((c) => c.id));

export function normalizeGoogleEventColorId(raw: string | null | undefined): GoogleEventColorId {
  const t = String(raw ?? "").trim();
  return VALID_IDS.has(t as GoogleEventColorId) ? (t as GoogleEventColorId) : "9";
}

export function googleEventColorHex(colorId: string | null | undefined): string {
  return GOOGLE_EVENT_COLORS.find((c) => c.id === normalizeGoogleEventColorId(colorId))?.hex ?? "#5484ed";
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/**
 * RGB -> CIE Lab. Necesario porque la distancia euclidiana en RGB confunde luminosidad
 * con matiz en tonos pastel (ej: un verde pálido queda "más cerca" de un amarillo pastel
 * que de un verde saturado en RGB, pero no perceptualmente). Lab es el espacio estándar
 * para comparar colores como los percibe un ojo humano.
 */
function rgbToLab([r, g, b]: [number, number, number]): [number, number, number] {
  const toLinear = (c: number) => {
    const v = c / 255;
    return v > 0.04045 ? Math.pow((v + 0.055) / 1.055, 2.4) : v / 12.92;
  };
  const [lr, lg, lb] = [toLinear(r), toLinear(g), toLinear(b)];
  const x = (lr * 0.4124 + lg * 0.3576 + lb * 0.1805) / 0.95047;
  const y = lr * 0.2126 + lg * 0.7152 + lb * 0.0722;
  const z = (lr * 0.0193 + lg * 0.1192 + lb * 0.9505) / 1.08883;
  const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const [fx, fy, fz] = [f(x), f(y), f(z)];
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

function labDistance(a: [number, number, number], b: [number, number, number]): number {
  return Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2);
}

export type GoogleEventColorMatch = {
  colorId: GoogleEventColorId;
  distance: number;
  runnerUpColorId: GoogleEventColorId;
  runnerUpDistance: number;
  /** true cuando el 2do candidato queda a menos del 10% de distancia relativa del mejor: no hay ganador claro. */
  ambiguous: boolean;
};

/**
 * Elige el colorId de Google Calendar (1–11) perceptualmente más parecido a un hex dado
 * (ej. el backgroundColor que Google asigna al calendario de un staff), usando distancia
 * CIE Lab, y reporta si el match es ambiguo (el segundo candidato está casi empatado).
 * Usado al auto-vincular un staff nuevo desde `importStaffFromGoogleCalendars`.
 *
 * Umbral de ambigüedad (0.90) calibrado contra casos reales: separa limpio un empate
 * genuino (ej. un naranja fuerte entre Tomate/Flamenco/Mandarina, ratio ~0.90-0.905)
 * de matches con ganador claro aunque no sean perfectos (ratio <= 0.84).
 */
export function nearestGoogleEventColorMatch(bgHex: string | null | undefined): GoogleEventColorMatch {
  const fallback: GoogleEventColorMatch = { colorId: "9", distance: 0, runnerUpColorId: "9", runnerUpDistance: 0, ambiguous: false };
  if (!bgHex || !/^#?[0-9a-fA-F]{6}$/.test(bgHex)) return fallback;
  try {
    const targetLab = rgbToLab(hexToRgb(bgHex));
    const ranked = GOOGLE_EVENT_COLORS
      .map((c) => ({ colorId: c.id, distance: labDistance(rgbToLab(hexToRgb(c.hex)), targetLab) }))
      .sort((a, b) => a.distance - b.distance);
    const [best, runnerUp] = ranked;
    const ambiguous = runnerUp.distance > 0 && best.distance / runnerUp.distance > 0.9;
    return { colorId: best.colorId, distance: best.distance, runnerUpColorId: runnerUp.colorId, runnerUpDistance: runnerUp.distance, ambiguous };
  } catch {
    return fallback;
  }
}

/**
 * Elige el colorId de Google Calendar (1–11) perceptualmente más parecido a un hex dado.
 * Wrapper simple sobre `nearestGoogleEventColorMatch` para callers que no necesitan saber
 * si el match fue ambiguo.
 */
export function nearestGoogleEventColorId(bgHex: string | null | undefined): GoogleEventColorId {
  return nearestGoogleEventColorMatch(bgHex).colorId;
}
